import React, { useState, useEffect, useContext } from 'react';
import { 
  Shield, Lock, Eye, EyeOff, Check, X, AlertTriangle, 
  ChevronRight, KeyRound, LogOut, Clock, AlertCircle, Save, Smartphone, Loader2
} from 'lucide-react';
import { clsx } from 'clsx';
import { useNavigate } from 'react-router-dom';
import planApi from '@api/planApi';
import { AuthContext } from '@context';

const STATUS_MAP = {
  ACTV: { label: "정상 (활성)", color: "text-green-700 bg-green-50 border-green-200" },
  LOCK: { label: "잠금", color: "text-red-700 bg-red-50 border-red-200" },
  DORM: { label: "휴면", color: "text-amber-700 bg-amber-50 border-amber-200" },
  WTHD: { label: "탈퇴/비활성", color: "text-slate-600 bg-slate-100 border-slate-200" },
};

const ROLE_MAP = {
  R0: "시스템 관리자",
  R2: "수행요원/사용자",
};

// 비밀번호 강도 측정 컴포넌트
function PasswordStrength({ password }) {
  const checks = [
    { label: "8자 이상", ok: password.length >= 8 },
    { label: "대소문자 포함", ok: /[a-zA-Z]/.test(password) },
    { label: "숫자 포함", ok: /[0-9]/.test(password) },
    { label: "특수문자 포함", ok: /[!@#$%^&*(),.?":{}|<>]/.test(password) },
  ];
  const score = checks.filter((c) => c.ok).length;
  const strength =
    score <= 1 ? { label: "매우 약함", color: "bg-red-500", w: "w-1/4" } :
    score <= 2 ? { label: "약함", color: "bg-orange-500", w: "w-2/4" } :
    score <= 3 ? { label: "보통", color: "bg-blue-500", w: "w-3/4" } :
                 { label: "안전함", color: "bg-green-500", w: "w-full" };

  if (!password) return null;

  return (
    <div className="mt-2 space-y-2">
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div className={clsx("h-full rounded-full transition-all duration-500", strength.color, strength.w)} />
        </div>
        <span className={clsx("text-xs font-medium", score <= 2 ? "text-red-500" : score === 3 ? "text-blue-600" : "text-green-600")}>
          {strength.label}
        </span>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {checks.map(({ label, ok }) => (
          <span key={label} className={clsx("flex items-center gap-1 text-xs", ok ? "text-green-600" : "text-slate-400")}>
            {ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />} {label}
          </span>
        ))}
      </div>
    </div>
  );
}

export function AccountManagement() {
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext); // 비활성화 시 로그아웃 처리용

  // 🌟 서버 데이터 상태
  const [accountInfo, setAccountInfo] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // 🌟 UI 상태
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [showPw, setShowPw] = useState({ current: false, next: false, confirm: false });
  const [pwSaveStatus, setPwSaveStatus] = useState("idle");
  const [pwError, setPwError] = useState("");
  
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [deactivateInput, setDeactivateInput] = useState("");

  // 서버에서 내 정보와 로그인 이력 가져오기
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [infoRes, historyRes] = await Promise.all([
          planApi.getMyInfo(),
          planApi.getLoginHistory()
        ]);
        setAccountInfo(infoRes);
        setSessions(historyRes || []);
      } catch (error) {
        alert("계정 정보를 불러오는데 실패했습니다.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const setPw = (key, value) => {
    setPwForm((p) => ({ ...p, [key]: value }));
    setPwError("");
  };
  const toggleShow = (key) => setShowPw((p) => ({ ...p, [key]: !p[key] }));

  const pwMatch = pwForm.next && pwForm.confirm && pwForm.next === pwForm.confirm;
  const pwMismatch = pwForm.next && pwForm.confirm && pwForm.next !== pwForm.confirm;

  // 🌟 비밀번호 변경 처리
  const handleChangePw = async (e) => {
    e.preventDefault();
    if (!pwForm.current || !pwForm.next || pwMismatch) return;
    
    setPwSaveStatus("saving");
    try {
      await planApi.changePassword({ currentPassword: pwForm.current, newPassword: pwForm.next });
      setPwSaveStatus("saved");
      setPwForm({ current: "", next: "", confirm: "" });
      setTimeout(() => setPwSaveStatus("idle"), 3000);
    } catch (error) {
      setPwSaveStatus("error");
      setPwError(error?.response?.data?.message || "비밀번호 변경 실패");
    }
  };

  // 🌟 계정 비활성화 처리
  const handleDeactivate = async () => {
    if (deactivateInput === accountInfo?.memberId) {
      try {
        await planApi.deactivateAccount();
        alert("계정이 비활성화 처리되었습니다. 안전하게 로그아웃됩니다.");
        logout(); // 🌟 쿠키 지우고 강제 로그아웃
      } catch (error) {
        alert("비활성화 처리 중 오류가 발생했습니다.");
      }
    }
  };

  if (isLoading || !accountInfo) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;
  }

  const status = STATUS_MAP[accountInfo.status || 'ACTV'] || STATUS_MAP['ACTV'];

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">
          <span>홈</span><ChevronRight className="h-3 w-3" /><span className="text-slate-600 font-medium">계정 관리</span>
        </div>
        <h2 className="text-xl font-bold text-slate-800">계정 관리</h2>
        <p className="text-sm text-slate-500 mt-0.5">비밀번호 변경, 계정 상태 및 보안 설정을 관리합니다.</p>
      </div>

      {/* 1. Account Status */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
        <h3 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2"><Shield className="h-4 w-4 text-blue-600" /> 계정 현황</h3>
        
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 mb-4">
          <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
            <p className="text-xs text-slate-400 mb-1.5">회원 ID</p>
            <p className="text-sm font-semibold text-slate-800 font-mono">{accountInfo.memberId}</p>
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
            <p className="text-xs text-slate-400 mb-1.5">계정 상태</p>
            <span className={clsx("inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium", status.color)}>
              {status.label}
            </span>
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
            <p className="text-xs text-slate-400 mb-1.5">역할 권한</p>
            <span className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 border border-blue-100 px-2.5 py-1 text-xs font-medium">
              {ROLE_MAP[accountInfo.roleId] || accountInfo.roleId}
            </span>
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
            <p className="text-xs text-slate-400 mb-1.5">수행요원 이름</p>
            <p className="text-sm font-semibold text-slate-800 font-mono">{accountInfo.staffNm || '-'}</p>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between rounded-xl bg-slate-50 border border-slate-100 px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-slate-700">로그인 활성화 상태</p>
            <p className="text-xs text-slate-400 mt-0.5">비활성화 시 해당 계정으로 시스템 접속이 차단됩니다.</p>
          </div>
          <div className={clsx("flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium", accountInfo.enabled === 1 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700")}>
            {accountInfo.enabled === 1 ? "활성화됨 (정상)" : "비활성화됨 (차단)"}
          </div>
        </div>
      </div>

      {/* 2. Password Change */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
        <h3 className="text-sm font-bold text-slate-700 mb-1 flex items-center gap-2"><KeyRound className="h-4 w-4 text-blue-600" /> 비밀번호 변경</h3>
        <p className="text-xs text-slate-400 mb-5">보안을 위해 영문, 숫자, 특수문자를 조합하여 설정해 주세요.</p>
        
        <form onSubmit={handleChangePw} className="space-y-4 max-w-md">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5">현재 비밀번호</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input type={showPw.current ? "text" : "password"} value={pwForm.current} onChange={(e) => setPw("current", e.target.value)} required className="h-10 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-10 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
              <button type="button" onClick={() => toggleShow("current")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {showPw.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5">새 비밀번호</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input type={showPw.next ? "text" : "password"} value={pwForm.next} onChange={(e) => setPw("next", e.target.value)} required className="h-10 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-10 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
              <button type="button" onClick={() => toggleShow("next")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {showPw.next ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <PasswordStrength password={pwForm.next} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5">새 비밀번호 확인</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input type={showPw.confirm ? "text" : "password"} value={pwForm.confirm} onChange={(e) => setPw("confirm", e.target.value)} required className={clsx("h-10 w-full rounded-lg border bg-white pl-9 pr-10 text-sm focus:outline-none focus:ring-1", pwMismatch ? "border-red-400 focus:ring-red-200" : pwMatch ? "border-green-400 focus:ring-green-200" : "border-slate-300 focus:ring-blue-500")} />
              <button type="button" onClick={() => toggleShow("confirm")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {showPw.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {pwMismatch && <p className="mt-1 text-xs text-red-500 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> 일치하지 않습니다.</p>}
            {pwError && <p className="mt-1 text-xs text-red-500 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> {pwError}</p>}
          </div>
          <div className="pt-2">
            <button type="submit" disabled={!pwForm.current || !pwForm.next || pwMismatch || pwSaveStatus === "saving"} className="flex h-10 w-full md:w-auto items-center justify-center gap-2 rounded-lg bg-blue-600 px-6 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50 transition-all">
              {pwSaveStatus === "saving" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} 비밀번호 변경
            </button>
          </div>
        </form>
      </div>

      {/* 3. Recent Sessions */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
        <h3 className="text-sm font-bold text-slate-700 mb-1 flex items-center gap-2"><Smartphone className="h-4 w-4 text-blue-600" /> 최근 접속 이력 (최대 5건)</h3>
        <p className="text-xs text-slate-400 mb-5">최근 로그인 시도 내역을 확인하세요. 의심스러운 내역이 있다면 즉시 비밀번호를 변경하세요.</p>
        
        <div className="space-y-3">
          {sessions.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">이력이 존재하지 않습니다.</p>
          ) : (
            sessions.map((s, idx) => (
              <div key={idx} className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 p-4">
                <div className="flex items-start gap-3">
                  <div className={clsx("flex h-9 w-9 items-center justify-center rounded-lg mt-0.5", s.loginStatus === 'SUCCESS' ? "bg-blue-600 text-white" : "bg-red-500 text-white")}>
                    <Smartphone className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-slate-800">{s.userAgent || s.deviceType || '알 수 없는 브라우저'}</p>
                      {idx === 0 && s.loginStatus === 'SUCCESS' && <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700 font-medium">최근 성공</span>}
                      {s.loginStatus !== 'SUCCESS' && <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700 font-medium">로그인 실패</span>}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">IP: {s.ipAddress} {s.location && `· ${s.location}`}</p>
                    <p className="text-xs text-slate-400">{new Date(s.loginTime).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* 4. Danger Zone */}
      <div className="rounded-2xl border border-red-200 bg-white shadow-sm p-6">
        <h3 className="text-sm font-bold text-red-600 mb-1 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> 위험 구역 (Danger Zone)</h3>
        <p className="text-xs text-slate-500 mb-5">계정을 비활성화하면 즉시 시스템에서 로그아웃 되며 재로그인이 불가능합니다.</p>

        <div className="flex items-center justify-between rounded-xl border border-red-100 bg-red-50/50 p-4">
          <div>
            <p className="text-sm font-semibold text-slate-800">계정 비활성화 (권한 반납)</p>
          </div>
          <button onClick={() => setShowDeactivateConfirm(true)} className="flex h-9 items-center gap-2 rounded-lg border border-red-300 bg-white px-4 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors">
            비활성화
          </button>
        </div>

        {showDeactivateConfirm && (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 animate-in fade-in">
            <p className="text-sm font-semibold text-red-700 mb-1">정말로 계정을 비활성화하시겠습니까?</p>
            <p className="text-xs text-slate-600 mb-3">확인을 위해 회원 ID <span className="font-mono font-bold">{accountInfo.memberId}</span> 를 입력해 주세요.</p>
            <div className="flex gap-2">
              <input type="text" value={deactivateInput} onChange={(e) => setDeactivateInput(e.target.value)} placeholder="회원 ID 입력" className="h-9 flex-1 rounded-lg border border-red-300 px-3 text-sm" />
              <button onClick={handleDeactivate} disabled={deactivateInput !== accountInfo.memberId} className="h-9 rounded-lg bg-red-600 px-4 text-sm font-medium text-white disabled:opacity-50">확인</button>
              <button onClick={() => { setShowDeactivateConfirm(false); setDeactivateInput(""); }} className="h-9 rounded-lg bg-white border border-slate-300 px-4 text-sm font-medium">취소</button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}